import React, { useState, useEffect, useMemo } from 'react';
import { brandsAPI } from '../services/api';
import './Modal.css';
import './BrandBrowserModal.css';

const BrandBrowserModal = ({ isOpen, onClose, onSelectBrand }) => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Load brands and categories when modal opens
  useEffect(() => {
    if (isOpen) {
      loadBrandsAndCategories();
    } else {
      // Reset state when modal closes
      setSearchTerm('');
      setSelectedCategory('');
    }
  }, [isOpen]);

  const loadBrandsAndCategories = async () => {
    setLoading(true);
    try {
      const [brandsData, categoriesData] = await Promise.all([
        brandsAPI.list(),
        brandsAPI.getCategories()
      ]);
      setBrands(brandsData);
      setCategories(categoriesData.categories || []);
    } catch (error) {
      console.error('Error loading brands:', error);
      alert('Failed to load brands. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter brands based on search and category
  const filteredBrands = useMemo(() => {
    return brands.filter(brand => {
      const matchesSearch = brand.brand_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory ||
        brand.brand_main_category === selectedCategory ||
        brand.brand_alt_category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [brands, searchTerm, selectedCategory]);

  const handleSelectBrand = async (brand) => {
    try {
      setLoading(true);
      const poisData = await brandsAPI.getPOIs(brand.brand_id);
      onSelectBrand(poisData);
      onClose();
    } catch (error) {
      console.error('Error loading brand POIs:', error);
      alert('Failed to load brand locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllInCategory = async () => {
    if (!selectedCategory) return;

    try {
      setLoading(true);
      // Load all brands in the selected category
      for (const brand of filteredBrands) {
        const poisData = await brandsAPI.getPOIs(brand.brand_id);
        onSelectBrand(poisData);
      }
      onClose();
    } catch (error) {
      console.error('Error loading category brands:', error);
      alert('Failed to load brands in category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large brand-browser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Brand Browser</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="brand-browser-filters">
            <div className="filter-group">
              <label htmlFor="search">Search Brands</label>
              <input
                id="search"
                type="text"
                placeholder="Type to search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="category">Filter by Category</label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {selectedCategory && filteredBrands.length > 0 && (
                <button
                  onClick={handleShowAllInCategory}
                  disabled={loading}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    width: '100%',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  Show All {filteredBrands.length} Brands in {selectedCategory}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="empty-state">
              <p>Loading brands...</p>
            </div>
          ) : filteredBrands.length === 0 ? (
            <div className="empty-state">
              <p>No brands found.</p>
              {(searchTerm || selectedCategory) && (
                <p className="empty-state-hint">Try adjusting your search or filter.</p>
              )}
            </div>
          ) : (
            <div className="brands-list">
              <div className="brands-list-header">
                <span>Found {filteredBrands.length} brand{filteredBrands.length !== 1 ? 's' : ''}</span>
              </div>
              {filteredBrands.map((brand) => (
                <div
                  key={brand.brand_id}
                  className="brand-item"
                  onClick={() => handleSelectBrand(brand)}
                >
                  <div className="brand-item-name">
                    {brand.brand_name}
                  </div>
                  <div className="brand-item-categories">
                    {brand.brand_main_category && (
                      <span className="category-badge primary">{brand.brand_main_category}</span>
                    )}
                    {brand.brand_alt_category && (
                      <span className="category-badge secondary">{brand.brand_alt_category}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default BrandBrowserModal;
